/**
 * Scoped PDP content enhancement — runs only inside product description
 * and product-page support content (delivery/payment accordions), never
 * globally. Does three things, safely:
 *  1. Converts known related-range phrases into internal links, first
 *     occurrence only, per container.
 *  2. Normalises stale phone numbers (01827 870385, 01827 214018, any
 *     spacing) to the canonical 01827 213073 / tel:01827213073, both in
 *     plain text and inside any existing <a href="tel:..."> that has a
 *     stale number.
 *  3. Links the canonical number wherever it appears as plain text.
 * Never touches text already inside an <a> or <button> (other than the
 * stale-anchor normalisation pass, which only rewrites known-stale
 * tel: hrefs, never a number it doesn't recognise).
 */
(function () {
  'use strict';

  var CONTAINER_SELECTOR = '.product-description, .disclosure__content, .product-details__block .rte';

  var CANONICAL_DISPLAY = '01827 213073';
  var CANONICAL_HREF = 'tel:01827213073';
  var STALE_DIGITS = ['01827870385', '01827214018'];

  var LINK_PHRASES = [
    { pattern: /\bRacking Accessories\b/i, url: '/collections/racking-accessories' },
    { pattern: /\bPallet Racking Frames\b/i, url: '/collections/palletracking' },
    { pattern: /\bPallet Racking Beams\b/i, url: '/collections/palletracking' },
    { pattern: /\bTimber Decks?\b/i, url: '/collections/pallet-racking-decking' },
    { pattern: /\bMesh Decks?\b/i, url: '/collections/pallet-racking-decking' },
    { pattern: /\bWarehouse Steps\b/i, url: '/collections/steps-and-ladders' },
    { pattern: /\bPallet Trucks?\b/i, url: '/collections/pallet-trucks' },
    { pattern: /\bRacking Installation\b/i, url: '/pages/pallet-racking-installation' },
    { pattern: /\bWarehouse Optimiser\b/i, url: '/pages/warehouse-optimiser' },
    { pattern: /\b(?:We Buy Racking|Sell Your (?:Pallet )?Racking)\b/i, url: '/pages/sell-my-pallet-racking' },
    { pattern: /\bGet a Quote\b/i, url: '/pages/get-a-quote' },
    { pattern: /\bSafety Barriers?\b/i, url: '/pages/rack-protection' },
    { pattern: /\bEnd Barriers?\b/i, url: '/pages/rack-protection' },
    { pattern: /\bFrame Protectors?\b/i, url: '/pages/rack-protection' },
    { pattern: /\bLeg Protectors?\b/i, url: '/pages/rack-protection' },
    { pattern: /\b(?:Racking|Rack) Protection\b/i, url: '/pages/rack-protection' }
  ];

  // Matches 01827 followed by 6 digits with optional single spaces
  // between groups, in the stale 3+3 shape (870 385) or the canonical
  // 3+3 shape (213 073) — either way we capture the raw digits and
  // decide stale vs canonical by value, not by spacing.
  var PHONE_PATTERN = /\b01827[ ]?(\d{3})[ ]?(\d{3})\b/g;

  function isInsideLinkOrButton(node) {
    var el = node.parentElement;
    return !!(el && el.closest('a, button'));
  }

  function textNodesIn(root) {
    var walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
      acceptNode: function (node) {
        if (!node.nodeValue || !node.nodeValue.trim()) {
          return NodeFilter.FILTER_REJECT;
        }
        if (isInsideLinkOrButton(node)) {
          return NodeFilter.FILTER_REJECT;
        }
        return NodeFilter.FILTER_ACCEPT;
      }
    });
    var nodes = [];
    var n;
    while ((n = walker.nextNode())) {
      nodes.push(n);
    }
    return nodes;
  }

  function wrapMatch(textNode, matchIndex, matchLength, buildAnchor) {
    var text = textNode.textContent;
    var before = text.slice(0, matchIndex);
    var matched = text.slice(matchIndex, matchIndex + matchLength);
    var after = text.slice(matchIndex + matchLength);

    var frag = document.createDocumentFragment();
    if (before) frag.appendChild(document.createTextNode(before));
    frag.appendChild(buildAnchor(matched));
    if (after) frag.appendChild(document.createTextNode(after));

    textNode.parentNode.replaceChild(frag, textNode);
  }

  // Rewrites <a href="tel:..."> elements whose digits match a known
  // stale number. Only touches recognised stale numbers — never
  // guesses at a number it doesn't know, so it can't clobber some
  // other legitimate contact number it hasn't seen before.
  function normaliseExistingAnchors(root) {
    var anchors = root.querySelectorAll('a[href^="tel:"]');
    anchors.forEach(function (a) {
      var href = a.getAttribute('href') || '';
      var digits = href.replace(/^tel:/, '').replace(/\D/g, '');
      if (STALE_DIGITS.indexOf(digits) !== -1) {
        a.setAttribute('href', CANONICAL_HREF);
        a.textContent = CANONICAL_DISPLAY;
      }
    });
  }

  function linkifyPhones(root) {
    var nodes = textNodesIn(root);
    for (var i = 0; i < nodes.length; i++) {
      var node = nodes[i];
      PHONE_PATTERN.lastIndex = 0;
      var match = PHONE_PATTERN.exec(node.textContent);
      if (!match) continue;

      var digits = '01827' + match[1] + match[2];
      var isStale = STALE_DIGITS.indexOf(digits) !== -1;

      wrapMatch(node, match.index, match[0].length, function (label) {
        var a = document.createElement('a');
        a.href = CANONICAL_HREF;
        a.textContent = isStale ? CANONICAL_DISPLAY : label;
        return a;
      });
    }
  }

  function linkifyPhrases(root) {
    LINK_PHRASES.forEach(function (entry) {
      var nodes = textNodesIn(root);
      for (var i = 0; i < nodes.length; i++) {
        var node = nodes[i];
        var match = entry.pattern.exec(node.textContent);
        if (!match) continue;

        wrapMatch(node, match.index, match[0].length, function (label) {
          var a = document.createElement('a');
          a.href = entry.url;
          a.textContent = label;
          return a;
        });
        break; // first occurrence only, per container, per phrase
      }
    });
  }

  function enhance() {
    var containers = document.querySelectorAll(CONTAINER_SELECTOR);
    containers.forEach(function (root) {
      if (root.dataset.rsiEnhanced === 'true') return;
      root.dataset.rsiEnhanced = 'true';
      normaliseExistingAnchors(root);
      linkifyPhrases(root);
      linkifyPhones(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', enhance);
  } else {
    enhance();
  }
})();
